/**
 * Fish names and field marks, second hundred: English.
 *
 * On why this file is separate, see species-world-ru.ts.
 *
 * ⚠️ THE NAME AN ANGLER WOULD TYPE, not a translation of the Latin. "Walleye",
 * "muskie", "barramundi", "mahseer" are the words in use; inventing "American
 * pikeperch" would hide the fish from the person looking for it.
 */
export const МИР_EN: Record<string, string> = {
  // ── Iberia and Italy ────────────────────────────────────────────────────
  'fish.iberian_barbel': 'Iberian barbel',
  'fish.iberian_barbel_look':
    'Long and olive-bronze, four barbels, mouth underneath. Holds in the pools below the rapids.',
  'fish.comizo_barbel': 'comizo barbel',
  'fish.comizo_barbel_look':
    'A long-snouted barbel with an almost pike-like mouth — the only one that hunts fry. Stays deep.',
  'fish.gitano_barbel': 'southern Iberian barbel',
  'fish.gitano_barbel_look':
    'Smaller and darker than its cousins, flank finely speckled. Comes onto baited ground in shoals.',
  'fish.ebro_barbel': 'Ebro barbel',
  'fish.ebro_barbel_look':
    'Thick-set, golden-grey, heavy-lipped. Pulls in the current harder than a carp twice its weight.',
  'fish.iberian_nase': 'Iberian nase',
  'fish.iberian_nase_look':
    'Silvery, dark-backed, with a hard crosswise mouth underneath. Scrapes stones in shoals on the run.',
  'fish.iberian_chub': 'southern Iberian chub',
  'fish.iberian_chub_look':
    'Smaller than the northern chub, large-scaled and dark-backed. Lies under overhanging bushes.',
  'fish.italian_barbel': 'Italian barbel',
  'fish.italian_barbel_look':
    'Streamlined, dark-spotted back, four barbels. Lies on the gravel runs of the Po tributaries.',
  'fish.italian_chub': 'Italian chub',
  'fish.italian_chub_look':
    'Broad-headed and silvery, each scale dark-edged. Takes off the top and fights at your feet.',
  'fish.italian_roach': 'Italian roach',
  'fish.italian_roach_look':
    'Small, silver, yellow-eyed. Packs into tight shoals in the canals of Lombardy.',
  'fish.italian_pike': 'Italian pike',
  'fish.italian_pike_look':
    'A southern pike: pale speckles along the flank instead of yellow bars. Holds in oxbows and lakes.',
  'fish.agone': 'agone shad',
  'fish.agone_look':
    'The shad of Alpine lakes: slim, silver, a dark blotch behind the gill cover. Cruises in midwater.',

  // ── The Balkans, the Danube and Mesopotamia ─────────────────────────────
  'fish.danube_shad': 'Pontic shad',
  'fish.danube_shad_look':
    'Silvery, keeled belly, a row of dark spots along the flank. Runs up the Danube every April.',
  'fish.softmouth_trout': 'softmouth trout',
  'fish.softmouth_trout_look':
    'A karst-river trout with a soft protruding snout and fine dark speckling. Holds in gin-clear water.',
  'fish.dentex_trout': 'dentex trout',
  'fish.dentex_trout_look':
    'A predatory Balkan trout, toothy and deep-bodied. Smashes into bleak on the surface of the pools.',
  'fish.ohrid_trout': 'Ohrid trout',
  'fish.ohrid_trout_look':
    'The silvery trout of Lake Ohrid, sparsely marked with black. Stays deep all year round.',
  'fish.schraetzer': 'schraetzer',
  'fish.schraetzer_look':
    'A narrow striped ruffe with a drawn-out snout: dark lines run the length of the flank. Bottom-hugging.',
  'fish.greek_catfish': 'Aristotle catfish',
  'fish.greek_catfish_look':
    'The catfish of the Epirus rivers: shorter than the wels, broad-headed, only two barbels.',
  'fish.tigris_barbel': 'mangar',
  'fish.tigris_barbel_look':
    'A huge Tigris and Euphrates barbel with a pike-like snout. Waits for fry on the floor of deep pools.',
  'fish.shabut': 'shabut',
  'fish.shabut_look':
    'A deep-bodied golden barbel with thick lips and large scales. Feeds on the bottom of warm rivers.',
  'fish.mesopotamian_catfish': 'Mesopotamian catfish',
  'fish.mesopotamian_catfish_look':
    'The catfish of the Tigris and Euphrates: paler than the wels and marbled. Lies in the deep holes.',

  // ── Western and Northern Europe ─────────────────────────────────────────
  'fish.blaufelchen': 'blaufelchen whitefish',
  'fish.blaufelchen_look':
    'The whitefish of Lake Constance, blue-backed with a small mouth. Suspends deep in open water.',
  'fish.alpine_char': 'Alpine char',
  'fish.alpine_char_look':
    'The char of mountain lakes: dark back, pink spots, white-edged lower fins. Wants ice-cold depth.',
  'fish.aquitanian_pike': 'Aquitanian pike',
  'fish.aquitanian_pike_look':
    'The pike of south-west France: smaller than the northern one, flank marbled olive. Waits in the weed.',
  'fish.danube_roach': 'Danube roach',
  'fish.danube_roach_look':
    'A large deep-bodied roach with orange fins. Travels the main channel of the Danube in shoals.',
  'fish.pearlfish': 'pearlfish',
  'fish.pearlfish_look':
    'A heavy roach of the pre-Alpine lakes; at spawning the flank breaks out in pearly tubercles.',
  'fish.minnow': 'minnow',
  'fish.minnow_look':
    'Tiny and mottled, dark blotches along the flank; the belly turns scarlet at spawning. Shoals in streams.',
  'fish.brook_lamprey': 'brook lamprey',
  'fish.brook_lamprey_look':
    'A small lamprey of clean streams: eel-shaped and jawless. As an adult it never feeds at all.',
  'fish.mediterranean_barbel': 'Mediterranean barbel',
  'fish.mediterranean_barbel_look':
    'A small barbel with a mottled flank and an orange tail. Holds on stony shallows.',
  'fish.apron': 'apron',
  'fish.apron_look':
    'A long cylindrical little perch of the Rhône, banded dark. Pressed flat to the bottom in fast water.',
  'fish.topmouth_gudgeon': 'topmouth gudgeon',
  'fish.topmouth_gudgeon_look':
    'A tiny invader with an upturned mouth and a dark stripe along the flank. Takes over quiet ponds.',
  'fish.peipus_whitefish': 'Peipus whitefish',
  'fish.peipus_whitefish_look':
    'The whitefish of Lake Peipus: silvery, straight-browed, mouth underneath. Feeds on the sand.',

  // ── Eastern Europe, the Caucasus, Central Asia and Siberia ──────────────
  'fish.amur_redfin': 'Amur redfin',
  'fish.amur_redfin_look':
    'Streamlined and silver, flat-headed, with crimson fins. Smashes into fry on the current seam.',
  'fish.mongolian_redfin': 'Mongolian redfin',
  'fish.mongolian_redfin_look':
    'An Amur predator with an upturned mouth and reddish fins. Hunts in the midwater of the side channels.',
  'fish.balkhash_marinka': 'Balkhash marinka',
  'fish.balkhash_marinka_look':
    'Dark olive and cylindrical, two pairs of barbels, mouth underneath. Feeds over stony bottom.',
  'fish.common_marinka': 'marinka',
  'fish.common_marinka_look':
    'A silver-grey fish of mountain rivers with thick lips. Takes fallen insects right on the current.',
  'fish.naked_osman': 'naked osman',
  'fish.naked_osman_look':
    'Almost scaleless, dark brown, barbels at the lower mouth. Holds among the rocks of Issyk-Kul.',
  'fish.altai_osman': 'Altai osman',
  'fish.altai_osman_look':
    'Thick-set and grey-green with a big head. In mountain lakes it turns predator and hunts small fish.',
  'fish.khramulya': 'khramulya',
  'fish.khramulya_look':
    'Dark back, silver flank, an underslung mouth with a horny edge. Scrapes algae off the stones.',
  'fish.bulatmai_barbel': 'bulatmai barbel',
  'fish.bulatmai_barbel_look':
    'A large barbel of the Kura and Aras, golden-grey and thick-lipped. Works the bottom in current.',
  'fish.pike_asp': 'pike asp',
  'fish.pike_asp_look':
    'The pike-shaped asp of the Amu Darya: long, silver, flat-headed. Hunts fry on the flooded shallows.',
  'fish.balkhash_perch': 'Balkhash perch',
  'fish.balkhash_perch_look':
    'A perch with no stripes: evenly grey, with a spiny crest. Holds by the weed and timber of Balkhash.',
  'fish.baikal_grayling': 'black Baikal grayling',
  'fish.baikal_grayling_look':
    'Dark, almost purple, with an enormous spotted sail of a dorsal. Holds over the rocks by the shore.',
  'fish.round_whitefish': 'round whitefish',
  'fish.round_whitefish_look':
    'A whitefish round in section with a small mouth underneath. Lies on the gravel of northern rivers.',
  'fish.humpback_whitefish': 'humpback whitefish',
  'fish.humpback_whitefish_look':
    'A humped whitefish with a flattened head and an underslung mouth. Feeds on the bed of Siberian rivers.',
  'fish.brown_bullhead': 'brown bullhead',
  'fish.brown_bullhead_look':
    'A brown little catfish with a square tail and eight barbels. Chokes small ponds and bites all year.',

  // ── Pacific salmon ──────────────────────────────────────────────────────
  'fish.chinook': 'chinook salmon',
  'fish.chinook_look':
    'The largest Pacific salmon: dark spotted back and black gums. Rests in the holes on the run upriver.',
  'fish.coho': 'coho salmon',
  'fish.coho_look':
    'Bright silver with a broad tail; in the river the flank floods crimson. Holds on the current.',
  'fish.chum': 'chum salmon',
  'fish.chum_look':
    'In the river it takes on purple bars across the body and the jaws hook. Runs the main channel.',
  'fish.pink_salmon': 'pink salmon',
  'fish.pink_salmon_look':
    'A small salmon with large black spots on the tail; the river male grows a hump. Runs in masses.',
  'fish.masu': 'masu salmon',
  'fish.masu_look':
    'A silver salmon with a pink band and dark blotches. The only one that lives in Asia alone.',
  'fish.sakhalin_taimen': 'Sakhalin taimen',
  'fish.sakhalin_taimen_look':
    'Dark back, reddish fins, a broad mouth. Holds in the estuaries and lower Sakhalin rivers.',

  // ── East Asia ───────────────────────────────────────────────────────────
  'fish.herabuna': 'herabuna crucian',
  'fish.herabuna_look':
    'A deep, flat crucian with a long anal fin. Feeds in midwater rather than off the bottom.',
  'fish.ugui': 'ugui dace',
  'fish.ugui_look':
    'The Japanese dace: silvery, with three orange bands along the flank at spawning. Travels in shoals.',
  'fish.hasu': 'hasu',
  'fish.hasu_look':
    'A small predator with a hooked mouth and a bluish sheen. Hunts fry at the surface of the lakes.',
  'fish.wuchang_bream': 'Wuchang bream',
  'fish.wuchang_bream_look':
    'A short deep bream with a straight back and fine scales. Holds in midwater of ponds and reservoirs.',
  'fish.chinese_culter': 'Chinese topmouth culter',
  'fish.chinese_culter_look':
    'Flat and silver, with a sharply upturned mouth and a straight back. Chases fry along the top.',
  'fish.barbel_steed': 'barbel steed',
  'fish.barbel_steed_look':
    'Streamlined, long-snouted, thick-lipped, with a pair of barbels. Roots on sandy bottom.',
  'fish.amur_wels': 'Amur catfish',
  'fish.amur_wels_look':
    'Smaller than the wels, four barbels, mottled flank. Lies in quiet channels under sunken timber.',
  'fish.biwa_catfish': 'Biwa catfish',
  'fish.biwa_catfish_look':
    'A big catfish of Lake Biwa, broad-mouthed and short-bodied. Comes onto the shallows at dusk.',
  'fish.mandarin_fish': 'mandarin fish',
  'fish.mandarin_fish_look':
    'A blotched perch with a spiny crest and a marbled flank. Waits in ambush among bottom stones.',
  'fish.leopard_mandarin': 'leopard mandarin fish',
  'fish.leopard_mandarin_look':
    'Smaller than the mandarin fish, the flank covered in round dark spots. Keeps to hill streams.',
  'fish.ayu': 'ayu',
  'fish.ayu_look':
    'Slender and translucent green, a yellow patch on the gill cover; smells of melon. Grazes the stones.',
  'fish.wakasagi': 'wakasagi smelt',
  'fish.wakasagi_look':
    'A tiny lake smelt of Japan: half-transparent with a silver band. Suspends in shoals in open water.',
  'fish.iwana': 'iwana char',
  'fish.iwana_look':
    'The char of Japanese streams: dark back, white spots, orange belly. Sits under stones and timber.',
  'fish.yamame': 'yamame trout',
  'fish.yamame_look':
    'The stream form of the masu: dark oval parr marks along the flank and red dots. Lives in cold brooks.',
  'fish.japanese_eel': 'Japanese eel',
  'fish.japanese_eel_look':
    'Snake-shaped, dark grey with a pale belly and tiny gill slits. Comes onto the shallows at night.',
  'fish.amur_sturgeon': 'Amur sturgeon',
  'fish.amur_sturgeon_look':
    'A sharp-snouted sturgeon with rows of bony scutes, darker than the Russian. Works the Amur bed.',

  // ── North America ───────────────────────────────────────────────────────
  'fish.walleye': 'walleye',
  'fish.walleye_look':
    'Milky reflective eyes, fangs, a white tip to the tail. Works the bottom along the drop-offs.',
  'fish.yellow_perch': 'yellow perch',
  'fish.yellow_perch_look':
    'Bright yellow with seven dark bars and orange pelvic fins. Moves in tight shoals.',
  'fish.muskellunge': 'muskellunge',
  'fish.muskellunge_look':
    'A huge pike, pale-flanked with dark bars and a forked tail. Waits in ambush along the weed.',
  'fish.black_crappie': 'black crappie',
  'fish.black_crappie_look':
    'Deep-bodied and silvery, speckled black with no pattern. Hangs in schools around sunken trees.',
  'fish.bluegill': 'bluegill',
  'fish.bluegill_look':
    'Round and gaudy, with a blue tab on the gill cover. Fusses about the weed and takes anything.',
  'fish.blue_catfish': 'blue catfish',
  'fish.blue_catfish_look':
    'Blue-grey, with a straight-edged anal fin and a forked tail. Holds the main channel in current.',
  'fish.flathead_catfish': 'flathead catfish',
  'fish.flathead_catfish_look':
    'Flattened head, jutting lower jaw, yellow-brown mottled flank. Waits inside log jams.',
  'fish.american_lake_trout': 'lake trout',
  'fish.american_lake_trout_look':
    'The big char of the Great Lakes: dark with pale spots and a deeply forked tail. Stays deep.',
  'fish.brook_char': 'brook trout',
  'fish.brook_char_look':
    'Dark marbled back, red dots in blue haloes, white leading edges to the lower fins.',
  'fish.striped_bass': 'striped bass',
  'fish.striped_bass_look':
    'Silver with seven dark stripes along the flank. Drives shoals of bait through open water.',
  'fish.longnose_gar': 'longnose gar',
  'fish.longnose_gar_look':
    'A long armoured body and a narrow toothed snout twice the length of the head. Hangs near the top.',
  'fish.alligator_gar': 'alligator gar',
  'fish.alligator_gar_look':
    'A giant in diamond-plated armour, with a short crocodile jaw carrying two rows of teeth.',
  'fish.lake_sturgeon': 'lake sturgeon',
  'fish.lake_sturgeon_look':
    'A dark blunt-snouted sturgeon with heavy scutes. Feeds on the floor of deep lakes and rivers.',
  'fish.white_sturgeon': 'white sturgeon',
  'fish.white_sturgeon_look':
    'The largest fish in North America: grey-white, four barbels ahead of a broad mouth.',

  // ── The tropics and the southern hemisphere ─────────────────────────────
  'fish.golden_mahseer': 'golden mahseer',
  'fish.golden_mahseer_look':
    'Golden scales the size of a palm, thick lips, a powerful tail. Holds in the pools of Himalayan rivers.',
  'fish.rohu': 'rohu',
  'fish.rohu_look':
    'Silver-blue with a bulging forehead and a downturned mouth. Feeds in the midwater of warm rivers.',
  'fish.siamese_carp': 'giant Siamese carp',
  'fish.siamese_carp_look':
    'A huge deep-bodied carp with an outsized head and large scales. Works the bed of the Mekong.',
  'fish.goonch': 'goonch',
  'fish.goonch_look':
    'A flattened catfish with a wide mouth, long barbels and a dark-blotched flank. Holds behind rocks.',
  'fish.wallago': 'wallago catfish',
  'fish.wallago_look':
    'A flat silvery catfish whose mouth reaches past the eye, with two barbels. Waits under the bank.',
  'fish.mekong_catfish': 'Mekong giant catfish',
  'fish.mekong_catfish_look':
    'A giant with no barbels and almost no teeth, steel-grey with small eyes. Cruises the Mekong midwater.',
  'fish.giant_pangasius': 'giant pangasius',
  'fish.giant_pangasius_look':
    'A dark pangasius with drawn-out thread-like fins and a tall dorsal ridge.',
  'fish.redtail_catfish': 'redtail catfish',
  'fish.redtail_catfish_look':
    'Black back, a white stripe along the flank and a bright red tail. Comes out to hunt after dark.',
  'fish.giant_snakehead': 'giant snakehead',
  'fish.giant_snakehead_look':
    'Snake head, very long dorsal fin, black-barred flank. Guards a red cloud of fry beneath it.',
  'fish.peacock_bass': 'peacock bass',
  'fish.peacock_bass_look':
    'Golden-green with three dark bars and an eyespot on the tail. Hits fry along the timber.',
  'fish.dorado': 'golden dorado',
  'fish.dorado_look':
    'Golden scales, red tail, a blunt head with a bony mouth. Jumps clear of the water like a salmon.',
  'fish.traira': 'traira',
  'fish.traira_look':
    'Cylindrical and brown, with a wide toothed mouth. Lies in ambush in the weed of small backwaters.',
  'fish.tambaqui': 'tambaqui',
  'fish.tambaqui_look':
    'Deep-bodied and blackish grey, with almost human teeth — it crushes nuts and seeds off the trees.',
  'fish.tigerfish': 'tigerfish',
  'fish.tigerfish_look':
    'A silver flank in lengthwise stripes and fangs that stay bared with the mouth shut.',
  'fish.goliath_tiger': 'goliath tigerfish',
  'fish.goliath_tiger_look':
    'The tigerfish of the Congo, as long as a person: thirty-two fangs and red fins.',
  'fish.nile_perch': 'Nile perch',
  'fish.nile_perch_look':
    'Silver-olive with a humped back and a yellow eye. Holds along deep drop-offs and rock.',
  'fish.barramundi': 'barramundi',
  'fish.barramundi_look':
    'Silver, with a concave forehead and a tall tail; the eye glows pink. Runs the estuaries and rivers.',
  'fish.murray_cod': 'Murray cod',
  'fish.murray_cod_look':
    'A heavy fish in green marbling with a wide mouth. Lives under the same snag for decades.',
  'fish.arapaima': 'arapaima',
  'fish.arapaima_look':
    'A giant in large red scales; it breathes air and surfaces noisily every quarter of an hour.',
};
